import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Skeleton from "react-loading-skeleton";
import { useEnvolvementAccess } from "../../hooks/useEnvolvementAccess";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { FiUsers, FiLink } from "react-icons/fi";

import Activity from "./envolvment/Activity";
import Submission from "./envolvment/Submission";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

function Envolvement() {
  const [ActivitySelected, setActivitySelected] = useState(true);
  const [SubmissionSelected, setSubmissionSelected] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const { hasAccess, loading: accessLoading } = useEnvolvementAccess(id || "");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPoster, setIsPoster] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    previewText: "",
    imgUrl: "",
    price: { price: "", free: true },
    quantity: { quantity: "", unlimited: true, currentEnvolves: 0 },
    categories: [],
    endDate: { endDate: "", none: true },
    targetEnvolvmentDate: "",
    active: true,
    isActive: true,
    submissions: {
      submissionsAllowed: true,
      submissionsActive: false,
      submissionsIDPair: "",
      submissionContent: {
        picture: false,
        description: false,
        url: false,
      },
    },
    published: {
      published: false,
      publishedReleaseDate: null,
    },
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${backend_domain}${backend_port}/post/${id}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch envolvement");
        }

        const postData = await response.json();
        setData(postData);
        setIsPoster(user?.sub === postData.userID);

        // Initialize edit form with current data
        setEditForm({
          title: postData.title || "",
          description: postData.description || "",
          previewText: postData.previewText || "",
          imgUrl: postData.imgUrl || "",
          price: {
            price: postData.price?.price || "",
            free: postData.price?.free ?? true,
          },
          quantity: {
            quantity: postData.quantity?.quantity || "",
            unlimited: postData.quantity?.unlimited ?? true,
            currentEnvolves: postData.quantity?.currentEnvolves || 0,
          },
          categories: postData.categories || [],
          endDate: {
            endDate: postData.endDate?.endDate || "",
            none: postData.endDate?.none ?? true,
          },
          targetEnvolvmentDate: postData.targetEnvolvmentDate || "",
          active: postData.active,
          isActive: postData.isActive,
          submissions: {
            submissionsAllowed:
              postData.submissions?.submissionsAllowed ?? true,
            submissionsActive: postData.submissions?.submissionsActive ?? false,
            submissionsIDPair: postData.submissions?.submissionsIDPair || "",
            submissionContent: {
              picture:
                postData.submissions?.submissionContent?.picture ?? false,
              description:
                postData.submissions?.submissionContent?.description ?? false,
              url: postData.submissions?.submissionContent?.url ?? false,
            },
          },
          published: {
            published: postData.published?.published ?? false,
            publishedReleaseDate:
              postData.published?.publishedReleaseDate || null,
          },
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, user?.sub]);

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const accessToken = await getAccessTokenSilently();

      // If activating submissions, call the specific endpoint first
      if (
        !data.submissions?.submissionsActive &&
        editForm.submissions?.submissionsActive
      ) {
        const submissionData = {
          picture: editForm.submissions.submissionContent.picture,
          description: editForm.submissions.submissionContent.description,
          url: editForm.submissions.submissionContent.url,
        };
        const openSubmissionsRes = await fetch(
          `${backend_domain}${backend_port}/user_submissions/open-submissions/${id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(submissionData),
          }
        );
        if (!openSubmissionsRes.ok) {
          throw new Error("Failed to open submissions.");
        }
      }

      const response = await fetch(
        `${backend_domain}${backend_port}/post/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update envolvement");
      }

      const updatedData = await response.json();
      setData(updatedData);
      setIsEditing(false);
      setError(null);
    } catch (error) {
      console.error("Error updating envolvement:", error);
      setError(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        `${backend_domain}${backend_port}/post/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete envolvement");
      }

      navigate("/profile"); // Redirect to home after successful deletion
    } catch (error) {
      console.error("Error deleting envolvement:", error);
      setError(error.message);
    }
  };

  const activityClick = () => {
    setActivitySelected(true);
    setSubmissionSelected(false);
  };

  const submissionClick = () => {
    setActivitySelected(false);
    setSubmissionSelected(true);
  };

  if (isLoading || accessLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton height={40} className="mb-4" />
          <Skeleton height={200} className="mb-6" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton height={100} />
            <Skeleton height={100} />
          </div>
        </div>
      </div>
    );
  }



  if (!data) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Envolvement Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The requested Envolvement could not be found.
          </p>
          <Link
            to="/explore"
            className="inline-block bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Calculate dates only when data is available
  const endDate = data.endDate?.endDate ? new Date(data.endDate.endDate) : null;
  const submissionsDeadlineDate = data.submissions?.submissionsDeadline
    ? new Date(data.submissions.submissionsDeadline)
    : null;
  const publishedReleaseDate = data.published?.publishedReleaseDate
    ? new Date(data.published.publishedReleaseDate)
    : null;
  const datePublished = data.datePublished
    ? new Date(data.datePublished)
    : null;
  const targetEnvolvmentDate = data.targetEnvolvmentDate
    ? new Date(data.targetEnvolvmentDate)
    : null;

  return (
    <div className="container mx-auto px-4 py-8">
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          {/* Hero Image Section */}
          <div className="relative">
            {data.imgUrl ? (
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={data.imgUrl}
                  alt={data.title}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            ) : (
              <div className="w-full h-[400px] bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400 text-lg">
                  No image available
                </span>
              </div>
            )}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <span
                className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  data.active
                    ? "bg-green-500 text-white"
                    : "bg-gray-500 text-white"
                }`}
              >
                {data.active ? "ACTIVE" : "INACTIVE"}
              </span>
              <div className="flex space-x-2">
                {isPoster && (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Title and Author Section */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-x-4">
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">
                    {data.title}
                  </h1>
                  <button
                    onClick={handleCopyLink}
                    className="relative rounded-full p-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                    title="Copy link"
                  >
                    <FiLink className="w-5 h-5 text-gray-500" />
                    {copied && (
                      <span className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg">
                        Copied!
                      </span>
                    )}
                  </button>
                </div>

                {/* Categories in header */}
                {data.categories && data.categories.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {data.categories.map((category) => (
                        <span
                          key={category}
                          className="px-2 py-1 bg-primary-50 text-primary-700 rounded-md text-xs font-medium border border-primary-200"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center text-gray-600">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full mr-3 border-2 border-gray-200 bg-gray-100 flex items-center justify-center">
                      {data.userPicture ? (
                        <img
                          src={data.userPicture}
                          alt={data.username}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-400 text-sm">
                          {data.username.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Posted by {data.username}</p>
                      {datePublished && (
                        <p className="text-sm text-gray-500">
                          {format(datePublished, "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                {data.price && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Price</p>
                    <p className="text-2xl font-bold text-primary-500">
                      {data.price.free ? "Free" : `${data.price.price}`}
                    </p>
                  </div>
                )}
                {data.quantity && (
                  <div>
                    <p className="text-sm text-gray-500">Spots Left</p>
                    <div className="flex items-center justify-end">
                      <FiUsers className="w-6 h-6 text-gray-500 mr-2" />
                      <p className="text-2xl font-bold text-gray-800">
                        {data.quantity.unlimited
                          ? "Unlimited"
                          : `${
                              data.quantity.quantity -
                              data.quantity.currentEnvolves
                            } / ${data.quantity.quantity}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="prose max-w-none mb-8">
              <p className="text-gray-700 text-lg leading-relaxed">
                {data.description}
              </p>
            </div>

            {/* Categories */}
            {/* {data.categories && data.categories.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Categories
                  </h3>
                  <span className="text-sm text-gray-500">
                    {data.categories.length} categor
                    {data.categories.length === 1 ? "y" : "ies"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {data.categories.map((category) => (
                    <span
                      key={category}
                      className="px-4 py-2 bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-shadow cursor-default"
                      title={`Category: ${category}`}
                    >
                      {category}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  These categories help users discover your Envolvement
                </p>
              </div>
            )} */}

            {/* Date Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {endDate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium">
                    {format(endDate, "MMM d, yyyy")}
                  </p>
                </div>
              )}
              {submissionsDeadlineDate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Submissions Deadline</p>
                  <p className="font-medium">
                    {format(submissionsDeadlineDate, "MMM d, yyyy")}
                  </p>
                </div>
              )}
              {publishedReleaseDate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Release Date</p>
                  <p className="font-medium">
                    {format(publishedReleaseDate, "MMM d, yyyy")}
                  </p>
                </div>
              )}
              {targetEnvolvmentDate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Target Date</p>
                  <p className="font-medium">
                    {format(targetEnvolvmentDate, "MMM d, yyyy")}
                  </p>
                </div>
              )}
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={activityClick}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    ActivitySelected
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Activity
                </button>
                {data.submissions?.submissionsAllowed && (
                  <button
                    onClick={submissionClick}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      SubmissionSelected
                        ? "border-primary-500 text-primary-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Submissions
                  </button>
                )}
              </nav>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {hasAccess || isPoster ? (
            <>
              {ActivitySelected && (
                <Activity
                  selected={ActivitySelected}
                  isPoster={isPoster}
                  envolvement={data}
                />
              )}
              {SubmissionSelected && (
                <Submission
                  isSelected={SubmissionSelected}
                  posterID={data.userID}
                  currentUserID={user?.sub}
                  isActive={data.submissions?.submissionsActive}
                  postID={id}
                  existingPostJSON={data.submissions?.submissionContent}
                  SubmissionIDPair={data.submissions?.submissionsIDPair}
                  submissionContent={data.submissions?.submissionContent}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Purchase Required
              </h3>
              <p className="text-gray-500 mb-6">
                Please purchase access to view the full content
              </p>

              {/* Show categories even for non-purchased content */}
              {data.categories && data.categories.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {data.categories.map((category) => (
                      <span
                        key={category}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate(`/payment/${id}`)}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Purchase Access
              </button>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">Edit Envolvement</h2>
              <form onSubmit={handleEdit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, title: e.target.value })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Preview Text
                    </label>
                    <input
                      type="text"
                      value={editForm.previewText}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          previewText: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Image URL
                    </label>
                    <input
                      type="text"
                      value={editForm.imgUrl}
                      onChange={(e) =>
                        setEditForm({ ...editForm, imgUrl: e.target.value })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Price
                    </label>
                    <input
                      type="number"
                      value={editForm.price.price}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          price: {
                            ...editForm.price,
                            price: e.target.value,
                            free: !e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={editForm.quantity.quantity}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          quantity: {
                            ...editForm.quantity,
                            quantity: e.target.value,
                            unlimited: !e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categories (Required - Select at least one)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        "Gaming",
                        "Interactive",
                        "Livestreamed",
                        "Video",
                        "Other",
                        "Art",
                        "Tutorials",
                        "Travel",
                        "Music",
                        "Q&A",
                        "Education",
                      ].map((category) => (
                        <label
                          key={category}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={editForm.categories.includes(category)}
                            onChange={(e) => {
                              const newCategories = e.target.checked
                                ? [...editForm.categories, category]
                                : editForm.categories.filter(
                                    (cat) => cat !== category
                                  );
                              setEditForm({
                                ...editForm,
                                categories: newCategories,
                              });
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">
                            {category}
                          </span>
                        </label>
                      ))}
                    </div>
                    {editForm.categories.length === 0 && (
                      <p className="text-red-500 text-xs mt-1">
                        Please select at least one category
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={editForm.endDate.endDate}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          endDate: {
                            ...editForm.endDate,
                            endDate: e.target.value,
                            none: !e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Target Date
                    </label>
                    <input
                      type="date"
                      value={editForm.targetEnvolvmentDate}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          targetEnvolvmentDate: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.active}
                      onChange={(e) =>
                        setEditForm({ ...editForm, active: e.target.checked })
                      }
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Submissions Settings
                    </label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.submissions?.submissionsActive}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              submissions: {
                                ...editForm.submissions,
                                submissionsActive: e.target.checked,
                              },
                            })
                          }
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Submissions Active
                        </label>
                      </div>
                      <div className="pl-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Submission Modes
                        </label>
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={
                                editForm.submissions?.submissionContent?.picture
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  submissions: {
                                    ...editForm.submissions,
                                    submissionContent: {
                                      ...editForm.submissions.submissionContent,
                                      picture: e.target.checked,
                                    },
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900">
                              Picture
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={
                                editForm.submissions?.submissionContent
                                  ?.description
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  submissions: {
                                    ...editForm.submissions,
                                    submissionContent: {
                                      ...editForm.submissions.submissionContent,
                                      description: e.target.checked,
                                    },
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900">
                              Text
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={
                                editForm.submissions?.submissionContent?.url
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  submissions: {
                                    ...editForm.submissions,
                                    submissionContent: {
                                      ...editForm.submissions.submissionContent,
                                      url: e.target.checked,
                                    },
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900">
                              URL
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Delete Envolvement</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this envolvement? This action
                cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Envolvement;
